import { useMemo, useRef } from "react";
import { useSectionScrollProgress } from "@/hooks/useSharedScroll";

/**
 * Manifesto — scroll-driven typographic statement.
 *
 * Perf model (v2, unified pattern):
 *  - NO continuous rAF loop, NO JS easing/smoothing.
 *  - Single-flight rAF throttle → one paint per scroll frame.
 *  - Direct 1:1 scroll → visual mapping. Perfect sync, zero drift.
 *  - Diff-cache prevents redundant DOM writes.
 *  - Zero React state on scroll path.
 */

const PHRASE = "YOU DESERVE TO BE HEARD";
const REPEAT_ROWS = 5;
const INITIAL_TILT = 32;
const ROW_SLIDE_VW = 140;

export default function Manifesto() {
  const sectionRef = useRef<HTMLElement>(null);

  const tiledLayerRef = useRef<HTMLDivElement>(null);
  const rowRefs = useRef<Array<HTMLDivElement | null>>([]);
  const headlineRef = useRef<HTMLHeadingElement>(null);
  const subtitleRef = useRef<HTMLParagraphElement>(null);

  const lastPaintedRef = useRef({
    rotation: -999,
    tiledOpacity: -1,
    headlineScale: -1,
    subtitleOpacity: -1,
    subtitleY: -999,
    rowOffsets: [] as number[],
  });

  const rowsMeta = useMemo(() => {
    const list: Array<{ dir: -1 | 1; delay: number }> = [];
    const middle = Math.floor(REPEAT_ROWS / 2);
    for (let i = 0; i < REPEAT_ROWS; i++) {
      const dir: -1 | 1 = i % 2 === 0 ? 1 : -1;
      const distanceFromMiddle = Math.abs(i - middle);
      const delay = distanceFromMiddle * 0.05;
      list.push({ dir, delay });
    }
    return list;
  }, []);
  const middleRow = Math.floor(REPEAT_ROWS / 2);

  const paint = (p: number) => {
    const rotateT = Math.max(0, Math.min(1, p / 0.6));
    const rotation = INITIAL_TILT * (1 - rotateT);

    const tiledFade = Math.max(0, Math.min(1, (p - 0.25) / 0.45));
    const tiledOpacity = 1 - tiledFade;

    const scaleT = Math.max(0, Math.min(1, (p - 0.1) / 0.55));
    const headlineScale = 0.9 + scaleT * 0.1;

    const subFade = Math.max(0, Math.min(1, (p - 0.72) / 0.2));
    const subtitleOpacity = subFade;
    const subtitleY = (1 - subFade) * 14;

    const last = lastPaintedRef.current;

    const tiled = tiledLayerRef.current;
    if (tiled) {
      if (Math.abs(rotation - last.rotation) > 0.05) {
        tiled.style.transform = `rotate(${rotation.toFixed(2)}deg)`;
        last.rotation = rotation;
      }
      if (Math.abs(tiledOpacity - last.tiledOpacity) > 0.005) {
        tiled.style.opacity = tiledOpacity.toFixed(3);
        last.tiledOpacity = tiledOpacity;
      }
    }

    for (let i = 0; i < rowsMeta.length; i++) {
      const meta = rowsMeta[i];
      const rowT = Math.max(
        0,
        Math.min(1, (p - meta.delay) / (0.55 - meta.delay)),
      );
      const eased = 1 - Math.pow(1 - rowT, 3);
      const offsetVw = meta.dir * ROW_SLIDE_VW * (1 - eased);
      const prev = last.rowOffsets[i];
      if (prev === undefined || Math.abs(offsetVw - prev) > 0.1) {
        const row = rowRefs.current[i];
        if (row)
          row.style.transform = `translate3d(${offsetVw.toFixed(2)}vw, 0, 0)`;
        last.rowOffsets[i] = offsetVw;
      }
    }

    const headline = headlineRef.current;
    if (headline && Math.abs(headlineScale - last.headlineScale) > 0.002) {
      headline.style.transform = `translate3d(0, 0, 0) scale(${headlineScale.toFixed(3)})`;
      last.headlineScale = headlineScale;
    }

    const subtitle = subtitleRef.current;
    if (subtitle) {
      if (Math.abs(subtitleOpacity - last.subtitleOpacity) > 0.005) {
        subtitle.style.opacity = subtitleOpacity.toFixed(3);
        last.subtitleOpacity = subtitleOpacity;
      }
      if (Math.abs(subtitleY - last.subtitleY) > 0.05) {
        subtitle.style.transform = `translate3d(0, ${subtitleY.toFixed(2)}px, 0)`;
        last.subtitleY = subtitleY;
      }
    }
  };

  useSectionScrollProgress(sectionRef, (p, inView) => {
    if (!inView) return;
    paint(p);
  });

  return (
    <section
      ref={sectionRef}
      id="manifesto"
      className="relative w-full bg-background-50"
      style={{
        height: "320vh",
        contain: "layout paint",
        isolation: "isolate",
      }}
    >
      <div
        className="sticky top-0 w-full h-screen overflow-hidden"
        style={{ contain: "layout paint" }}
      >
        {/* Section label */}
        <div className="absolute top-0 left-0 right-0 z-30 px-6 md:px-10 pt-28 md:pt-32 pointer-events-none">
          <div className="max-w-[1600px] mx-auto flex items-baseline justify-between">
            <p className="text-[10px] tracking-[0.35em] uppercase text-foreground-950/60 font-medium">
              Our Belief
            </p>
            <p className="text-[10px] tracking-[0.35em] uppercase text-foreground-950/60 font-medium hidden md:block">
              A quiet manifesto
            </p>
          </div>
        </div>

        {/* Tiled repeated phrase */}
        <div
          ref={tiledLayerRef}
          aria-hidden="true"
          className="absolute z-10 flex flex-col items-center justify-center pointer-events-none"
          style={{
            transform: `rotate(${INITIAL_TILT}deg)`,
            opacity: 1,
            width: "220%",
            height: "220%",
            left: "-60%",
            top: "-60%",
            transformOrigin: "50% 50%",
            willChange: "transform, opacity",
            backfaceVisibility: "hidden",
          }}
        >
          {rowsMeta.map((_, i) => {
            const isMiddle = i === middleRow;
            return (
              <div
                key={`row-${i}`}
                ref={(el) => {
                  rowRefs.current[i] = el;
                }}
                className="w-full whitespace-nowrap text-center leading-[0.95] select-none"
                style={{
                  fontFamily: "var(--font-heading)",
                  fontSize: "clamp(3.5rem, 8vw, 8.5rem)",
                  fontWeight: 400,
                  letterSpacing: "-0.03em",
                  fontVariationSettings: "'opsz' 144",
                  color: isMiddle
                    ? "oklch(var(--foreground-950) / 0.14)"
                    : "oklch(var(--foreground-950) / 0.1)",
                  paddingBlock: "0.05em",
                  transform: `translate3d(${
                    rowsMeta[i].dir * ROW_SLIDE_VW
                  }vw, 0, 0)`,
                  willChange: "transform",
                  backfaceVisibility: "hidden",
                }}
              >
                {PHRASE}
              </div>
            );
          })}
        </div>

        {/* Centered headline */}
        <div className="absolute inset-0 z-20 flex items-center justify-center px-6 pointer-events-none">
          <div className="flex flex-col items-center gap-8 md:gap-10 max-w-[1400px] w-full">
            <h2
              ref={headlineRef}
              className="text-center leading-[0.95] whitespace-nowrap"
              style={{
                fontFamily: "var(--font-heading)",
                fontSize: "clamp(3.5rem, 8vw, 8.5rem)",
                fontWeight: 400,
                letterSpacing: "-0.03em",
                fontVariationSettings: "'opsz' 144",
                color: "oklch(var(--foreground-950))",
                transform: "translate3d(0, 0, 0) scale(0.9)",
                willChange: "transform",
                backfaceVisibility: "hidden",
              }}
            >
              {PHRASE}
            </h2>

            <p
              ref={subtitleRef}
              className="font-heading italic text-center max-w-[720px]"
              style={{
                fontSize: "clamp(1.05rem, 1.45vw, 1.4rem)",
                lineHeight: 1.55,
                fontWeight: 300,
                letterSpacing: "0.005em",
                color: "oklch(var(--foreground-950) / 0.7)",
                opacity: 0,
                transform: "translate3d(0, 14px, 0)",
                fontVariationSettings: "'opsz' 144",
                willChange: "transform, opacity",
              }}
            >
              Not everything needs to be solved. Some things just need to be truly seen.
            </p>
          </div>
        </div>

        {/* Corner meta */}
        <div className="absolute bottom-8 left-6 md:left-10 z-30 flex items-center gap-3 text-foreground-950/55">
          <span className="text-[10px] tracking-[0.3em] uppercase font-medium">
            Chapter 02
          </span>
          <span className="w-8 h-[1px] bg-foreground-950/40"></span>
          <span className="text-[10px] tracking-[0.3em] uppercase font-medium">
            Belief
          </span>
        </div>

        <div className="absolute bottom-8 right-6 md:right-10 z-30 flex items-center gap-3 text-foreground-950/55">
          <span className="text-[10px] tracking-[0.3em] uppercase font-medium">
            Scroll to reveal
          </span>
          <span className="w-8 h-[1px] bg-foreground-950/40"></span>
        </div>
      </div>
    </section>
  );
}